<template>
	<div class="fixed inset-0 z-50 flex items-center justify-center modal-backdrop" @click.self="close">
		<div class="bg-white rounded shadow-lg w-full max-w-5xl modal-content">
			<div class="flex justify-between items-center px-8 py-5 border-b">
				<div class="text-xl font-bold">
					{{ $t('accident_input_data') }}
				</div>

				<button type="button" class="text-gray-500 cursor-pointer" @click="close">
					<font-awesome-icon :icon="['fas', 'times']" size="lg" />
				</button>
			</div>

			<div v-if="!isLoading" class="px-8 py-6 modal-body">
				<div class="text-left">
					<div class="text-lg mb-4 font-bold">
						{{ $t('accident_data') }}
					</div>

					<DamageAssessorCaseAccidentDataForm ref="AccidentDataForm" v-model="accident" />

					<div class="text-lg mt-8 mb-4 font-bold">
						{{ $t('opponent_data') }}
					</div>

					<DamageAssessorCaseOppositePersonForm ref="OppositePersonForm" v-model="oppositeParty" />
				</div>
			</div>

			<div v-else class="flex justify-center py-16">
				<LoadingSpinner />
			</div>

			<div v-if="!isLoading" class="flex justify-end px-8 py-5 border-t">
				<BaseButton @click="close" class="mr-2">
					{{ $t('general.cancel') }}
				</BaseButton>

				<BaseButton @click="save" :disabled="isSaving" class="btn-blue">
					{{ $t('general.save') }}
				</BaseButton>
			</div>
		</div>
	</div>
</template>

<script>
import BaseButton from '@/components/forms/buttons/BaseButton.vue';
import LoadingSpinner from '@/components/navigation/LoadingSpinner.vue';

import DamageAssessorCaseAccidentDataForm from '@/components/damageassessorcases/forms/DamageAssessorCaseAccidentDataForm.vue';
import DamageAssessorCaseOppositePersonForm from '@/components/damageassessorcases/forms/DamageAssessorCaseOppositePersonForm.vue';

import DamageAssessorCaseRepository from '@/api/repositories/DamageAssessorCaseRepository';

export default {
	name: 'DamageAssessorCaseEditAccident',

	components: {
		BaseButton,
		LoadingSpinner,

		DamageAssessorCaseAccidentDataForm,
		DamageAssessorCaseOppositePersonForm,
	},

	data() {
		return {
			isLoading: true,
			isSaving: false,
			damageAssessorCase: null,
			accident: null,
			oppositeParty: null,
		};
	},

	mounted() {
		this.load();
	},

	methods: {
		load() {
			this.isLoading = true;

			new DamageAssessorCaseRepository()
				.getDamageAssessorCaseDetail(this.$route.params.id)
				.then(response => {
					this.damageAssessorCase = response.data;
					if (this.damageAssessorCase?.broker_case) {
						this.close();
						return;
					}

					this.loadAccidentData();
					this.loadOppositePartyData();
				})
				.catch(error => {
					this.showError();

					this.close();
				})
				.finally(() => {
					this.isLoading = false;
				});
		},

		loadAccidentData() {
			this.accident = {
				date: this.damageAssessorCase?.date_of_accident ?? null,
				damageTimeSetByUser: this.damageAssessorCase?.damage_time_set_by_user ?? false,
				accidentLocation: this.damageAssessorCase?.accident_location ?? null,
				isRecordedByPolice: this.damageAssessorCase?.is_recorded_by_police ?? null,
				isPersonInjured: this.damageAssessorCase?.is_person_injured ?? null,
				accidentReport: this.findAccidentReport(),
			};
		},

		loadOppositePartyData() {
			const oppositePerson = this.damageAssessorCase?.opposite_person ?? null;

			this.oppositeParty = {
				oppositePerson: oppositePerson
					? {
							firstName: oppositePerson.firstName ?? oppositePerson.first_name ?? null,
							lastName: oppositePerson.lastName ?? oppositePerson.last_name ?? null,
					  }
					: null,

				oppositeLicensePlate: {
					text: this.damageAssessorCase?.causer_license_plate ?? null,

					isSpecial: this.normalizeBoolean(this.damageAssessorCase?.is_causer_license_plate_special),
				},

				insuranceName: this.damageAssessorCase?.opposite_insurance_name ?? null,
				insuranceNumber: this.damageAssessorCase?.opposite_insurance_number ?? null,
			};
		},
		normalizeBoolean(value) {
			return value === true || value === 1 || value === '1';
		},
		findAccidentReport() {
			const media = this.damageAssessorCase?.media ?? [];

			return media.find(item => item.category === 'accident_report') ?? null;
		},

		hasOppositePerson() {
			const person = this.oppositeParty?.oppositePerson;

			return !!(person && (person.firstName || person.lastName));
		},

		buildDto() {
			const dto = {
				damage_assessor_case: this.buildDamageAssessorCaseDto(),
			};

			const oppositePerson = this.buildOppositePersonDto();

			if (oppositePerson) {
				dto.opposite_person = oppositePerson;
			}
			return dto;
		},

		buildDamageAssessorCaseDto() {
			return {
				date_of_accident: this.accident?.date ?? null,
				damage_time_set_by_user: this.accident?.damageTimeSetByUser ?? false,
				accident_location: this.accident?.accidentLocation ?? null,
				is_recorded_by_police: this.accident?.isRecordedByPolice ?? null,
				is_person_injured: this.accident?.isPersonInjured ?? null,
				causer_license_plate: this.oppositeParty?.oppositeLicensePlate?.text ?? null,
				is_causer_license_plate_special: this.oppositeParty?.oppositeLicensePlate?.isSpecial ?? false,
				opposite_insurance_name: this.oppositeParty?.insuranceName ?? null,
				opposite_insurance_number: this.oppositeParty?.insuranceNumber ?? null,
			};
		},

		buildOppositePersonDto() {
			const person = this.oppositeParty?.oppositePerson;

			if (!person || (!person.firstName && !person.lastName)) {
				return null;
			}

			return {
				firstName: person.firstName ?? null,
				lastName: person.lastName ?? null,
			};
		},

		save() {
			if (this.isSaving || !this.$refs.AccidentDataForm || !this.$refs.OppositePersonForm) {
				return;
			}

			const isAccidentValid = this.$refs.AccidentDataForm.validate();
			const isOpponentValid = this.$refs.OppositePersonForm.validate();

			if (!isAccidentValid || !isOpponentValid) {
				this.showValidationError();
				return;
			}

			this.isSaving = true;

			new DamageAssessorCaseRepository()
				.updateDamageAssessorCase(this.damageAssessorCase.id, this.buildDto())
				.then(() => {
					this.$root.$emit('updateCase');
					return this.$swal({
						title: this.$t('general.success').toString(),
						text: this.$t('general.changes_saved').toString(),
						icon: 'success',
					});
				})
				.then(() => {
					this.close();
				})
				.catch(error => {
					this.showError();
				})
				.finally(() => {
					this.isSaving = false;
				});
		},

		close() {
			this.$router.push({
				name: 'damageAssessorCaseDetail',
				params: {
					id: this.$route.params.id,
				},
			});
		},

		showValidationError() {
			this.$swal(this.$t('general.attention').toString(), this.$t('please_check_input').toString(), 'warning');
		},

		showError() {
			this.$swal(this.$t('general.error').toString(), this.$t('general.changes_not_saved').toString(), 'error');
		},
	},
};
</script>

<style scoped>
.modal-backdrop {
	background: rgba(0, 0, 0, 0.45);
	padding: 2rem;
}

.modal-content {
	max-height: calc(100vh - 4rem);
}

.modal-body {
	max-height: calc(100vh - 12rem);
	overflow-y: auto;
}
</style>
